// frontend/src/pages/ProfileFlowersPage.js

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  CaretCircleLeft,
  CaretCircleRight,
  PlusCircle,
  MegaphoneSimple,
} from "phosphor-react";

import { api } from "../api";
import BackButton from "../components/BackButton";
import ProfileFlowers from "../components/ProfileFlowers";
import { getShoutoutFont } from "../constants/shoutoutFonts";

const SHOUTOUTS_PER_PAGE = 12;

/*
 * Small variations so the mobile page still feels
 * like a handwritten / yearbook wall instead of
 * a rigid grid.
 */
const MOBILE_MESSAGE_STYLES = [
  { rotate: "-2deg", y: "0px" },
  { rotate: "2deg", y: "8px" },

  { rotate: "1deg", y: "-4px" },
  { rotate: "-2deg", y: "6px" },

  { rotate: "-1deg", y: "3px" },
  { rotate: "3deg", y: "-5px" },

  { rotate: "2deg", y: "7px" },
  { rotate: "-1deg", y: "-3px" },

  { rotate: "-2deg", y: "4px" },
  { rotate: "2deg", y: "-5px" },

  { rotate: "1deg", y: "5px" },
  { rotate: "-2deg", y: "0px" },
];

function getAuthHeaders() {
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : undefined;
}

function getDisplayName(user) {
  return (
    user?.display_name ||
    user?.name ||
    user?.username ||
    "this person"
  );
}

function MobileShoutout({
  shoutout,
  index,
}) {
  const senderName =
    shoutout?.sender?.username ||
    shoutout?.sender?.name ||
    "someone";

  const shoutoutFont = getShoutoutFont(
    shoutout?.font_id
  );

  const position =
    MOBILE_MESSAGE_STYLES[
      index % MOBILE_MESSAGE_STYLES.length
    ];

  return (
    <div
      className="
        flex min-w-0
        flex-col
        items-center
        justify-center
        text-center
        px-1
      "
      style={{
        transform: `
          translateY(${position.y})
          rotate(${position.rotate})
        `,
      }}
    >
      {shoutout?.prefix && (
        <p
          className="
            max-w-full
            text-[9px]
            font-bold
            uppercase
            leading-tight
            tracking-[0.08em]
            text-blue-500
          "
        >
          {shoutout.prefix}
        </p>
      )}

      <p
        className="
          mt-0.5
          max-w-full
          whitespace-pre-wrap
          break-words
          text-[17px]
          leading-[1.1]
          text-[#25366f]
        "
        style={{
          fontFamily: shoutoutFont.css,
          fontWeight: shoutoutFont.weight,
        }}
      >
        {shoutout?.body}
      </p>

      <p
        className="
          mt-1
          max-w-full
          truncate
          text-[10px]
          font-medium
          leading-none
          text-gray-400
        "
      >
        @{senderName}
      </p>
    </div>
  );
}

export default function ProfileFlowersPage() {
  const { id } = useParams();

  const profileUserId = Number(id);

  const [profileUser, setProfileUser] =
    useState(null);

  const [viewer, setViewer] =
    useState(null);

  const [shoutouts, setShoutouts] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [page, setPage] =
    useState(0);

  /*
   * Used only to tell ProfileFlowers:
   * "open your existing Give Shoutout composer".
   *
   * Incrementing the number means we don't have
   * to move/copy all of that modal code into here.
   */
  const [giveSignal, setGiveSignal] =
    useState(0);

  /*
   * Re-fetch wall after a shoutout is successfully
   * submitted through ProfileFlowers.
   */
  const [refreshKey, setRefreshKey] =
    useState(0);

  const displayName =
    getDisplayName(profileUser);

  const isSelf =
    viewer?.id &&
    profileUser?.id &&
    Number(viewer.id) ===
      Number(profileUser.id);

  const canGiveShoutout =
    !!profileUser &&
    !!viewer &&
    !isSelf &&
    profileUser?.flowers_enabled !== false;

  /*
   * Load profile user + viewer.
   */
  useEffect(() => {
    if (!profileUserId) {
      setLoading(false);
      setError("Could not load this profile.");
      return;
    }

    let cancelled = false;

    const loadUsers = async () => {
      const headers = getAuthHeaders();

      try {
        const [
          profileResponse,
          viewerResponse,
        ] = await Promise.all([
          api.get(
            `/users/${profileUserId}`,
            {
              headers,
            }
          ),

          api
            .get("/users/me", {
              headers,
            })
            .catch(() => ({
              data: null,
            })),
        ]);

        if (cancelled) return;

        setProfileUser(
          profileResponse?.data || null
        );

        setViewer(
          viewerResponse?.data || null
        );
      } catch (err) {
        if (cancelled) return;

        setProfileUser(null);

        setError(
          err?.response?.data?.detail ||
            "Could not load this profile."
        );
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [profileUserId]);

  /*
   * Load shoutouts.
   */
  useEffect(() => {
    if (!profileUserId) return;

    let cancelled = false;

    const loadShoutouts = async () => {
      setLoading(true);
      setError("");

      try {
        const headers = getAuthHeaders();

        const response = await api.get(
          `/users/${profileUserId}/flowers`,
          {
            headers,
          }
        );

        if (cancelled) return;

        const items =
          Array.isArray(response?.data)
            ? response.data
            : [];

        setShoutouts(items);

        /*
         * Normal page opening:
         * start on page 1.
         *
         * After sending a new shoutout:
         * jump to the newest page because we display
         * the wall chronologically oldest -> newest.
         */
        if (refreshKey > 0) {
          const visibleCount =
            items.filter(
              (item) =>
                !item.hidden_by_recipient
            ).length;

          const newestPage =
            Math.max(
              0,
              Math.ceil(
                visibleCount /
                  SHOUTOUTS_PER_PAGE
              ) - 1
            );

          setPage(newestPage);
        }
      } catch (err) {
        if (cancelled) return;

        setShoutouts([]);

        setError(
          err?.response?.data?.detail ||
            "Could not load shoutouts."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadShoutouts();

    return () => {
      cancelled = true;
    };
  }, [
    profileUserId,
    refreshKey,
  ]);

  /*
   * Same hidden filtering as the existing modal.
   */
  const visibleShoutouts = useMemo(
    () =>
      shoutouts.filter(
        (shoutout) =>
          !shoutout.hidden_by_recipient
      ),
    [shoutouts]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(
      visibleShoutouts.length /
        SHOUTOUTS_PER_PAGE
    )
  );

  /*
   * Your existing wall displays chronologically:
   * oldest pages first, newest page last.
   */
  const chronologicalShoutouts =
    useMemo(
      () =>
        [
          ...visibleShoutouts,
        ].reverse(),
      [visibleShoutouts]
    );

  const pageShoutouts =
    useMemo(() => {
      const start =
        page *
        SHOUTOUTS_PER_PAGE;

      return chronologicalShoutouts.slice(
        start,
        start +
          SHOUTOUTS_PER_PAGE
      );
    }, [
      chronologicalShoutouts,
      page,
    ]);

  /*
   * If data changes and current page
   * disappears, fall back safely.
   */
  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(
        Math.max(
          0,
          totalPages - 1
        )
      );
    }
  }, [
    page,
    totalPages,
  ]);

  const goPrevious = () => {
    setPage((current) =>
      Math.max(
        0,
        current - 1
      )
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const goNext = () => {
    setPage((current) =>
      Math.min(
        totalPages - 1,
        current + 1
      )
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const goToPage = (
    nextPage
  ) => {
    setPage(nextPage);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleGiveShoutout =
    () => {
      setGiveSignal(
        (value) => value + 1
      );
    };

  const handleShoutoutSent =
    () => {
      setRefreshKey(
        (value) => value + 1
      );
    };

  return (
    <div className="min-h-screen bg-blue-50">
      {/* SAME MOBILE HEADER STYLE AS EventCreatePage */}
      <header
        className="
          sticky
          left-0 right-0 top-0
          z-30
          bg-blue-50
        "
        style={{
          paddingTop:
            "env(safe-area-inset-top)",
        }}
      >
        <div
          className="
            mt-2
            flex
            items-start
            px-4
          "
        >
          <BackButton
            className="text-blue-600"
            ariaLabel="Back"
          />

          <div className="mt-2.5 flex min-w-0 flex-1 items-start pr-4">
            {/* TITLE + LONG SQUIGGLE */}
            <div className="min-w-0 flex-1">
              <h1
                className="
                  whitespace-nowrap
                  text-[20px]
                  text-blue-600
                  dm-font
                  font-black
                  tracking-[-0.06em]
                  leading-[0.9]
                  select-none
                "
              >
                Shoutouts
              </h1>

              <svg
                viewBox="0 0 300 16"
                className="
                  -mt-0.5
                  block
                  h-3
                  w-full
                  text-blue-500
                "
                aria-hidden="true"
                preserveAspectRatio="none"
              >
                <path
                  d="
                    M 2 8
                    Q 17 0, 32 8
                    T 62 8
                    T 92 8
                    T 122 8
                    T 152 8
                    T 182 8
                    T 212 8
                    T 242 8
                    T 272 8
                    T 298 8
                  "
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            {/* GIVE SHOUTOUT */}
            {canGiveShoutout && (
              <button
                type="button"
                onClick={handleGiveShoutout}
                aria-label="Give a shoutout"
                title="Give a shoutout"
                className="
                  ml-3
                  -mt-1
                  inline-flex
                  shrink-0
                  items-center
                  justify-center
                  text-blue-600
                  !min-h-0
                  !min-w-0
                  transition
                  hover:text-blue-800
                  hover:scale-105
                "
              >
                <PlusCircle
                  size={28}
                  weight="duotone"
                />
              </button>
            )}
          </div>
        </div>
      </header>

      <main
        className="
          flex
          min-h-[calc(100svh-90px)]
          flex-col
          px-5
          pb-6
          pt-5
        "
        style={{
          paddingBottom:
            "calc(env(safe-area-inset-bottom) + 24px)",
        }}
      >
        {loading ? (
          <div
            className="
              flex
              min-h-[55vh]
              items-center
              justify-center
            "
          >
            <p
              className="
                text-sm
                text-gray-500
              "
            >
              Loading shoutouts…
            </p>
          </div>
        ) : error ? (
          <div
            className="
              flex
              min-h-[55vh]
              items-center
              justify-center
              text-center
            "
          >
            <p
              className="
                text-sm
                text-red-600
              "
            >
              {error}
            </p>
          </div>
        ) : visibleShoutouts.length ===
          0 ? (
          <div
            className="
              flex
              min-h-[55vh]
              flex-col
              items-center
              justify-center
              text-center
            "
          >
            <p
              className="
                text-sm
                font-semibold
                text-gray-700
              "
            >
              No shoutouts yet
            </p>

            {canGiveShoutout && (
              <button
                type="button"
                onClick={
                  handleGiveShoutout
                }
                className="
                  mt-3
                  inline-flex
                  items-center
                  gap-1
                  rounded-full
                  bg-blue-300
                  px-3
                  py-1
                  text-xs
                  font-semibold
                  text-blue-800
                  ring-2
                  ring-blue-800
                  !min-h-0
                  !min-w-0
                "
              >
                <MegaphoneSimple
                  size={14}
                  weight="duotone"
                />

                Give a shoutout
              </button>
            )}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            {/* SHOUTOUTS */}
            <div
              className="
                mx-auto
                grid
                w-full
                max-w-md
                grid-cols-2
                content-start
                gap-x-4
                gap-y-6
                pt-0.5
                pb-2
              "
            >
              {pageShoutouts.map(
                (shoutout, index) => (
                  <MobileShoutout
                    key={shoutout.id}
                    shoutout={shoutout}
                    index={index}
                  />
                )
              )}
            </div>

            {/* PAGE CONTROLS — stays at bottom */}
            {totalPages > 1 && (
              <div
                className="
                  fixed
                  left-0
                  right-0
                  z-20
                  flex
                  items-center
                  justify-center
                  gap-3
                  bottom-[calc(env(safe-area-inset-bottom)+68px)]
                "
              >
           
                <button
                  type="button"
                  onClick={
                    goPrevious
                  }
                  disabled={
                    page === 0
                  }
                  className="
                    inline-flex
                    items-center
                    justify-center
                    text-blue-600
                    transition
                    !min-h-0
                    !min-w-0
                    hover:text-blue-800
                    disabled:cursor-default
                    disabled:opacity-25
                  "
                  aria-label="Previous shoutouts page"
                >
                  <CaretCircleLeft
                    size={26}
                    weight="duotone"
                  />
                </button>

                <div
                  className="
                    flex
                    items-center
                    gap-1.5
                  "
                >
                  {Array.from(
                    {
                      length:
                        totalPages,
                    },
                    (
                      _,
                      index
                    ) => (
                      <button
                        key={
                          index
                        }
                        type="button"
                        onClick={() =>
                          goToPage(
                            index
                          )
                        }
                        aria-label={`Go to shoutouts page ${
                          index +
                          1
                        }`}
                        className={`
                          h-1.5
                          w-1.5
                          rounded-full
                          transition
                          !min-h-0
                          !min-w-0
                          ${
                            page ===
                            index
                              ? "bg-blue-600"
                              : "bg-blue-200 hover:bg-blue-300"
                          }
                        `}
                      />
                    )
                  )}
                </div>

                <button
                  type="button"
                  onClick={
                    goNext
                  }
                  disabled={
                    page ===
                    totalPages -
                      1
                  }
                  className="
                    inline-flex
                    items-center
                    justify-center
                    text-blue-600
                    transition
                    !min-h-0
                    !min-w-0
                    hover:text-blue-800
                    disabled:cursor-default
                    disabled:opacity-25
                  "
                  aria-label="Next shoutouts page"
                >
                  <CaretCircleRight
                    size={26}
                    weight="duotone"
                  />
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {profileUser && (
        <ProfileFlowers
          profileUser={
            profileUser
          }
          currentUser={
            viewer
          }
          isSelf={isSelf}
          readOnly={false}
          hideWallLauncher
          openGiveSignal={
            giveSignal
          }
          openWallAfterSend={
            false
          }
          onShoutoutSent={
            handleShoutoutSent
          }
        />
      )}
    </div>
  );
}